import channels from 'store/app/entities/channels/action';
import MessagesApi, { messagesApiUtil } from 'store/app/entities/MessagesApiSlice';
import { MESSAGES_PER_PAGE } from 'store/app/entities/messagesListMerge';
import currentChannelId from 'store/selectors/currentChannelId';

export const CATCH_UP_INTERVAL_MS = 5000;

const OBJECT_ID = /^[a-f0-9]{24}$/i;
const MAX_CATCH_UP_PAGES = 10;

let lastCatchUpAt = 0;
let seenSocketConnect = false;

export function shouldCatchUpInbox({
  now,
  lastCatchUpAt: previousAt,
  intervalMs = CATCH_UP_INTERVAL_MS,
}) {
  return !previousAt || now - previousAt >= intervalMs;
}

/** Returning to a visible tab. The first focus of a page load is not a return. */
export function focusCatchUpDecision({ focus, hadFocus, socketConnected }) {
  if (!focus || hadFocus) return 'none';
  if (!socketConnected) return 'connect';
  return 'catchUp';
}

/** First connect of this page is the initial load. Later connects are reconnects. */
export function consumeSocketConnect() {
  const isReconnect = seenSocketConnect;
  seenSocketConnect = true;
  return isReconnect;
}

export function resetInboxCatchUpState() {
  lastCatchUpAt = 0;
  seenSocketConnect = false;
}

/** Newest persisted message. Optimistic rows use uniqid() and are not valid fromId anchors. */
export function newestCatchUpFromId(messages) {
  const row = (messages || []).find(
    message => !message?.temp && OBJECT_ID.test(String(message?._id || '')),
  );
  return row?._id || null;
}

const catchUpOpenChannel = async (dispatch, getState, channelId) => {
  const selected = MessagesApi.endpoints.listMessages.select({ channelId })(getState());
  let fromId = newestCatchUpFromId(selected?.data?.messages);
  if (!fromId) {
    if (selected?.status === 'fulfilled') {
      dispatch(messagesApiUtil.invalidateTags([{ type: 'Messages', id: channelId }]));
    }
    return;
  }

  for (let page = 0; page < MAX_CATCH_UP_PAGES; page += 1) {
    const request = dispatch(
      MessagesApi.endpoints.listMessages.initiate(
        { channelId, fromId },
        { forceRefetch: true, subscribe: false },
      ),
    );
    try {
      const data = await request.unwrap();
      const count = data?.messages?.length ?? 0;
      if (count < MESSAGES_PER_PAGE) return;
      const pageNewest = newestCatchUpFromId(data.messages);
      if (!pageNewest || pageNewest === fromId) return;
      fromId = pageNewest;
    } finally {
      request.unsubscribe();
    }
  }
};

const catchUpInbox = () => async (dispatch, getState) => {
  const now = Date.now();
  if (!shouldCatchUpInbox({ now, lastCatchUpAt })) return;

  dispatch(channels.getMemberChannels());

  const channelId = currentChannelId(getState());
  if (!channelId) {
    lastCatchUpAt = Date.now();
    return;
  }

  try {
    await catchUpOpenChannel(dispatch, getState, channelId);
    lastCatchUpAt = Date.now();
  } catch {
    // Leave the throttle unset so the next focus retries the open thread.
  }
};

export default catchUpInbox;
